import { z } from 'zod';

// US States for validation
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
] as const;

// Phone number regex: accepts formats like (123) 456-7890, 123-456-7890, 1234567890
const PHONE_REGEX = /^(\+1)?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

// UUID regex for Amplify organization
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Mulesoft organisation EUID: MB_ followed by 18 alphanumeric characters
const EUID_REGEX = /^MB_[A-Za-z0-9]{18}$/;

export const newOrganizationSchema = z.object({
  organizationName: z
    .string()
    .min(1, 'Organization name is required')
    .max(255, 'Organization name must be 255 characters or less'),
  streetAddress: z
    .string()
    .min(1, 'Street address is required')
    .max(500, 'Street address must be 500 characters or less'),
  addressLine2: z
    .string()
    .max(500, 'Address line 2 must be 500 characters or less')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .min(1, 'City is required')
    .max(100, 'City must be 100 characters or less'),
  state: z
    .string()
    .length(2, 'State must be a 2-letter code')
    .refine((val) => US_STATES.includes(val as typeof US_STATES[number]), {
      message: 'Invalid state code',
    }),
  zipCode: z
    .string()
    .regex(/^\d{5}(-\d{4})?$/, 'Zip code must be 5 digits or 9 digits (12345 or 12345-6789)'),
  ownerFirstName: z
    .string()
    .min(1, 'Owner first name is required')
    .max(100, 'First name must be 100 characters or less'),
  ownerLastName: z
    .string()
    .min(1, 'Owner last name is required')
    .max(100, 'Last name must be 100 characters or less'),
  ownerEmail: z
    .string()
    .min(1, 'Owner email is required')
    .email('Invalid email address'),
  ownerPhone: z
    .string()
    .regex(PHONE_REGEX, 'Invalid phone number format')
    .optional()
    .or(z.literal('')),
  billing: z
    .enum(['easyTithe', 'Legacy'], { message: 'Billing type must be easyTithe or Legacy' }),
  sendInvite: z
    .boolean()
    .default(true),
  euid: z
    .string()
    .regex(EUID_REGEX, "EUID must be 'MB_' followed by 18 alphanumeric characters")
    .optional()
    .or(z.literal('')),
});

export type NewOrganizationFormData = z.infer<typeof newOrganizationSchema>;

// EUID is optional for admins but required for operators. The requiredness is
// layered on via superRefine so the inferred form type stays identical to the
// base schema (which wizardStep1Schema also consumes).
export function makeNewOrganizationSchema(options: { euidRequired: boolean }) {
  return newOrganizationSchema.superRefine((data, ctx) => {
    if (options.euidRequired && (!data.euid || data.euid === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['euid'],
        message: 'EUID is required',
      });
    }
  });
}

/**
 * Canonical form of an Amplify organization UUID: trimmed and lowercase.
 *
 * Amplify and the source-system modules treat the UUID as a case-sensitive path segment,
 * so an uppercase paste has to be normalised before it is stored in the wizard or sent to
 * the API. The backend applies the same rule at every boundary; this keeps what the
 * operator sees in the wizard identical to what gets persisted.
 */
export function normalizeAmplifyOrganizationUuid(value: string): string {
  return value.trim().toLowerCase();
}

export const existingOrganizationSchema = z.object({
  amplifyOrganizationUuid: z
    .string()
    .min(1, 'Organization UUID is required')
    .regex(UUID_REGEX, 'Invalid UUID format'),
});

export type ExistingOrganizationFormData = z.infer<typeof existingOrganizationSchema>;

// Helper function to format phone number for display
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

// Helper function to normalize phone number for API
export function normalizePhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  return phone;
}
